/**
 * Application shell.
 *
 * One sidebar, rendered once. The Base44 layout duplicated the entire sidebar
 * markup twice — a desktop copy and a mobile copy, around 130 lines each — and
 * switched between them with an injected style block full of !important
 * overrides, plus a view-mode toggle that faked a window resize.
 */

import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LogOut, Menu, Monitor, Moon, Sun } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { BrandMark } from '@/components/BrandMark.jsx';
import { VersionBadge } from '@/components/VersionBadge.jsx';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth-context.jsx';
import { useTheme } from '@/lib/theme-context.jsx';
import { visibleRoutes } from '@/routes.jsx';

function NavItems({ onNavigate }) {
  const { can } = useAuth();
  const items = visibleRoutes(can);

  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {items.map((route) => {
        const Icon = route.icon;
        return (
          <NavLink
            key={route.path}
            to={route.path}
            onClick={onNavigate}
            // NavLink matches the whole path, so "/products" no longer also
            // highlights a future "/products-archive". The original used
            // pathname.includes(url), which matched nothing at all on the
            // landing page and collides on any shared prefix.
            className={({ isActive }) =>
              cn(
                'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors coarse:py-3',
                isActive
                  ? 'bg-white/10 text-white'
                  : 'text-sidebar-foreground/75 hover:bg-white/5 hover:text-white',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* Gold marker on the active item. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-amber-400 transition-opacity',
                    isActive ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <Icon
                  className={cn(
                    'h-4 w-4 shrink-0 transition-colors',
                    isActive
                      ? 'text-amber-400'
                      : 'text-sidebar-foreground/60 group-hover:text-white',
                  )}
                />
                <span className="truncate">{route.title}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  const options = [
    { value: 'light', icon: Sun, label: 'Light' },
    { value: 'dark', icon: Moon, label: 'Dark' },
    { value: 'system', icon: Monitor, label: 'System' },
  ];

  return (
    <div className="flex gap-1 rounded-lg bg-white/5 p-1" role="group" aria-label="Colour theme">
      {options.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => setTheme(value)}
          aria-pressed={theme === value}
          title={label}
          className={cn(
            'flex flex-1 items-center justify-center rounded-md py-1.5 transition-colors coarse:py-2.5',
            theme === value
              ? 'bg-white/15 text-amber-400'
              : 'text-sidebar-foreground/60 hover:text-white',
          )}
        >
          <Icon className="h-4 w-4" />
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}

function SidebarBody({ onNavigate }) {
  const { user, signOut } = useAuth();
  const label = user?.display_name || user?.full_name || user?.email || '';

  return (
    // Safe-area padding: installed to an iPhone home screen the page runs
    // under the status bar and above the home indicator (viewport-fit=cover),
    // so the brand row and Sign out need moving clear of both.
    <div className="brand-gradient flex h-full flex-col pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pt-[env(safe-area-inset-top)] text-sidebar-foreground">
      <div className="flex items-center gap-3 px-5 py-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white ring-1 ring-white/15">
          <BrandMark className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight text-white">StockWise</p>
          <p className="truncate text-xs text-sidebar-foreground/70">Inventory management</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-4">
        <NavItems onNavigate={onNavigate} />
      </div>

      <div className="space-y-3 border-t border-white/10 p-3">
        <ThemeToggle />

        <div className="flex items-center gap-3 px-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400/90 text-xs font-semibold text-amber-950">
            {(label || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{label}</p>
            <p className="truncate text-xs text-sidebar-foreground/60">{user?.email}</p>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-sidebar-foreground/80 hover:bg-white/10 hover:text-white"
          onClick={signOut}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </Button>

        <VersionBadge />
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { can } = useAuth();
  const current = visibleRoutes(can).find((route) => route.path === location.pathname);

  return (
    // dvh, not vh: on iPad and iPhone Safari 100vh is taller than what is
    // visible while the toolbars show, which clipped the sidebar's footer.
    <div className="flex min-h-dvh bg-background">
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="sticky top-0 h-dvh">
          <SidebarBody />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* The header grows by the status-bar height on a notched iPhone,
            and keeps clear of the notch sideways in landscape. */}
        <header className="sticky top-0 z-30 flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center gap-3 border-b bg-card/90 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[env(safe-area-inset-top)] backdrop-blur lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              // The close button sits on the dark brand gradient here.
              className="w-72 border-0 p-0 [&>button]:top-[calc(1rem+env(safe-area-inset-top))] [&>button]:text-white"
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SidebarBody onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-2">
            <BrandMark className="h-5 w-5 text-primary" />
            <span className="font-semibold">{current?.title || 'StockWise'}</span>
          </Link>
        </header>

        <main className="min-w-0 flex-1 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] lg:pl-0">
          {children}
        </main>
      </div>
    </div>
  );
}

/** Consistent page heading used by every screen. */
export function PageHeader({ title, description, actions, icon: Icon }) {
  return (
    <div className="page-wash border-b bg-card/60">
      <div className="flex flex-col gap-3 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex items-start gap-3">
          {Icon ? (
            <div className="hidden h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/20 sm:flex">
              <Icon className="h-5 w-5" />
            </div>
          ) : null}
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
            {description ? (
              <div className="mt-0.5 text-sm text-muted-foreground">{description}</div>
            ) : null}
          </div>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

export function PageBody({ children, className }) {
  return <div className={cn('px-4 py-5 sm:px-6', className)}>{children}</div>;
}

/** Empty-state block, so every list looks the same when it has nothing. */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-card/40 px-6 py-14 text-center">
      {Icon ? (
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-50 to-violet-100 text-indigo-500 dark:from-indigo-950 dark:to-violet-950 dark:text-indigo-300">
          <Icon className="h-7 w-7" />
        </div>
      ) : null}
      <p className="font-medium">{title}</p>
      {description ? (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
